import { cva } from 'class-variance-authority'
import { useState } from 'react'

import { Button } from '@/legacy-src/components/ui/button'
import { cn } from '@/legacy-src/lib/utils'

import { type ServiceType } from '../../types/GeneralStateType'
import useOutsideClick from '../../utilities/useClickOutside'
import Icon from '../icons/Icon'

export type DropdownMenuAlign = 'left' | 'center' | 'right'

type DropdownItemBaseProps = {
  readonly text: string
  readonly value?: string | ServiceType
  readonly icon?: string
}

type DropdownItemWithServiceProps = DropdownItemBaseProps & {
  readonly onClick: (value?: ServiceType) => void
}

type DropdownItemWithCallBackProps = DropdownItemBaseProps & {
  readonly onClick?: (value?: string) => void
}

type DropdownItemProps = {
  readonly text: string
  readonly onClick: () => void
  readonly value?: string | ServiceType
  readonly icon?: string
}

export type DropdownMenuProps = {
  readonly dataTestId: string
  readonly className?: string
  readonly mainItem?: DropdownItemWithServiceProps | DropdownItemWithCallBackProps
  readonly listItems?: DropdownItemWithServiceProps[] | DropdownItemWithCallBackProps[]
  readonly buttonSize?: 'small' | 'default' | 'medium'
  readonly small?: boolean
  readonly align?: DropdownMenuAlign
}

const menuVariants = cva(
  'absolute z-10 w-max min-w-full max-w-[min(20rem,calc(100vw-2rem))] rounded-2xl bg-white shadow-lg',
  {
    variants: {
      align: {
        left: 'left-0',
        center: 'left-1/2 -translate-x-1/2',
        right: 'right-0',
      },
    },
  },
)

const DropdownItem: React.FC<DropdownItemProps> = ({ text, icon, onClick }) => {
  return (
    <li className={`flex items-center transition-opacity first:rounded-t-2xl last:rounded-b-2xl hover:bg-white`}>
      <Button
        variant="inline"
        size="small"
        className={`${icon} m-0 flex min-w-0 grow justify-start !px-6 !py-4 text-left text-sm hover:text-text-color focus:shadow-none`}
        onClick={onClick}
      >
        {icon && <Icon color="primary" icon={icon} size="sm" className="mr-3 shrink-0" />}
        <span className="min-w-0 break-words pl-2">{text}</span>
      </Button>
    </li>
  )
}

const DropdownMenu: React.FC<DropdownMenuProps> = ({
  dataTestId,
  className,
  mainItem,
  listItems,
  buttonSize,
  small = true,
  align = 'left',
}) => {
  const [isOpen, setIsOpen] = useState(false)

  const toggleOpen = () => setIsOpen((prev) => !prev)

  const handleClickOutside = () => setIsOpen(false)
  const ref = useOutsideClick(handleClickOutside)

  return (
    <div className={cn('relative', className)} id="more-options" ref={ref}>
      <Button
        variant="light"
        size={small ? (buttonSize ?? 'small') : 'default'}
        onClick={toggleOpen}
        className="w-full"
      >
        {mainItem.icon && <Icon color="primary" icon={mainItem.icon} size="sm" className={`mr-4`} />}
        {mainItem.text}
        <Icon color="primary" icon="selectArrow" size="xs" className={`ml-2 ${isOpen ? 'rotate-180' : null}`} />
      </Button>
      {isOpen && (
        <div className={menuVariants({ align })}>
          <ul className="m-0 list-none divide-x-0 divide-y-2 divide-dashed divide-[#EBE2D9] p-0">
            {listItems.map((item, key) => {
              const onClickOption = () => {
                item.onClick(item.value)
                handleClickOutside()
              }
              if (item.text != mainItem.text) {
                return (
                  <DropdownItem
                    key={`${dataTestId}-${key}`}
                    text={item.text}
                    icon={item.icon}
                    onClick={onClickOption}
                  />
                )
              }
            })}
          </ul>
        </div>
      )}
    </div>
  )
}

export default DropdownMenu
